import {
  BorderStyleProps,
  LayoutStyleProps,
  PositionStyleProps,
  ScrollStyleProps,
  SizeStyleProps,
  SpaceStyleProps,
  TableStyleProps,
  TextStyleProps,
  BackgroundStyleProps,
  BoxShadowStyleProps,
  ListStyleProps,
  StylePropKeys,
  MediaStyleProps,
  CursorStyleProps,
  FlexItemLayoutStyleProps,
  GridContainerLayoutStyleProps,
  GridItemLayoutStyleProps,
  PointerEventsStyleProps,
  TransitionStyleProps,
  OpacityStyleProps,
  TransformStyleProps,
  OverscrollStyleProps,
} from '../types';

export const borderStyleProps: Record<keyof BorderStyleProps, boolean> = {
  borderWidth: true,
  borderBlockWidth: true,
  borderBlockStartWidth: true,
  borderBlockEndWidth: true,
  borderInlineWidth: true,
  borderInlineStartWidth: true,
  borderInlineEndWidth: true,
  borderColor: true,
  borderBlockColor: true,
  borderBlockStartColor: true,
  borderBlockEndColor: true,
  borderInlineColor: true,
  borderInlineStartColor: true,
  borderInlineEndColor: true,
  borderStyle: true,
  borderBlockStyle: true,
  borderBlockStartStyle: true,
  borderBlockEndStyle: true,
  borderInlineStyle: true,
  borderInlineStartStyle: true,
  borderInlineEndStyle: true,
  borderRadius: true,
  borderStartStartRadius: true,
  borderStartEndRadius: true,
  borderEndStartRadius: true,
  borderEndEndRadius: true,
};

export const layoutStyleProps: Record<keyof LayoutStyleProps, boolean> = {
  display: true,
  float: true,
  overflow: true,
  overflowX: true,
  overflowY: true,
  flexDirection: true,
  justifyContent: true,
  alignItems: true,
  flexWrap: true,
  alignSelf: true,
  justifySelf: true,
  flex: true,
  flexBasis: true,
  flexGrow: true,
  flexShrink: true,
  order: true,
  gap: true,
  rowGap: true,
  columnGap: true,
  visibility: true,
  clipPath: true,
  containerType: true,
  containerName: true,
};

export const flexItemLayoutStyleProps: Record<
  keyof FlexItemLayoutStyleProps,
  boolean
> = {
  alignSelf: true,
  justifySelf: true,
  flex: true,
  flexBasis: true,
  flexGrow: true,
  flexShrink: true,
  order: true,
};

export const gridContainerLayoutStyleProps: Record<
  keyof GridContainerLayoutStyleProps,
  boolean
> = {
  gridTemplateColumns: true,
  gridTemplateRows: true,
  gridTemplateAreas: true,
  gridTemplate: true,
  columnGap: true,
  rowGap: true,
  gridColumnGap: true,
  gridRowGap: true,
  justifyItems: true,
  alignItems: true,
  justifyContent: true,
  alignContent: true,
  gridAutoColumns: true,
  gridAutoRows: true,
  gridAutoFlow: true,
  grid: true,
};

export const gridItemLayoutStyleProps: Record<
  keyof GridItemLayoutStyleProps,
  boolean
> = {
  gridColumnStart: true,
  gridColumnEnd: true,
  gridRowStart: true,
  gridRowEnd: true,
  gridColumn: true,
  gridRow: true,
  gridArea: true,
  justifySelf: true,
  alignSelf: true,
};

export const positionStyleProps: Record<keyof PositionStyleProps, boolean> = {
  position: true,
  inset: true,
  insetBlockStart: true,
  insetBlockEnd: true,
  insetInlineStart: true,
  insetInlineEnd: true,
  zIndex: true,
};

export const sizeStyleProps: Record<keyof SizeStyleProps, boolean> = {
  blockSize: true,
  minBlockSize: true,
  maxBlockSize: true,
  inlineSize: true,
  minInlineSize: true,
  maxInlineSize: true,
};

export const spaceStyleProps: Record<keyof SpaceStyleProps, boolean> = {
  padding: true,
  paddingInline: true,
  paddingInlineStart: true,
  paddingInlineEnd: true,
  paddingBlock: true,
  paddingBlockStart: true,
  paddingBlockEnd: true,
  margin: true,
  marginInline: true,
  marginInlineStart: true,
  marginInlineEnd: true,
  marginBlock: true,
  marginBlockStart: true,
  marginBlockEnd: true,
};

export const tableStyleProps: Record<keyof TableStyleProps, boolean> = {
  borderCollapse: true,
  borderSpacing: true,
  captionSide: true,
  emptyCells: true,
  tableLayout: true,
};

export const textStyleProps: Record<keyof TextStyleProps, boolean> = {
  color: true,
  fontStyle: true,
  textAlign: true,
  textTransform: true,
  textDecoration: true,
  textOverflow: true,
  wordBreak: true,
  typography: true,
  overflowWrap: true,
  whiteSpace: true,
  textWrap: true,
};

export const transitionStyleProps: Record<keyof TransitionStyleProps, boolean> =
  {
    transition: true,
    transitionProperty: true,
    transitionDuration: true,
    transitionTimingFunction: true,
    transitionDelay: true,
  };

export const backgroundStyleProps: Record<keyof BackgroundStyleProps, boolean> =
  {
    backgroundColor: true,
  };

export const boxShadowStyleProps: Record<keyof BoxShadowStyleProps, boolean> = {
  boxShadow: true,
};

export const listStyleProps: Record<keyof ListStyleProps, boolean> = {
  listStyleType: true,
  listStylePosition: true,
};

export const mediaStyleProps: Record<keyof MediaStyleProps, boolean> = {
  objectFit: true,
};

export const cursorStyleProps: Record<keyof CursorStyleProps, boolean> = {
  cursor: true,
};

export const pointerEventsStyleProps: Record<
  keyof PointerEventsStyleProps,
  boolean
> = {
  pointerEvents: true,
};

export const scrollStyleProps: Record<keyof ScrollStyleProps, boolean> = {
  scrollMarginBlockStart: true,
};

export const opacityStyleProps: Record<keyof OpacityStyleProps, boolean> = {
  opacity: true,
};

export const overscrollStyleProps: Record<keyof OverscrollStyleProps, boolean> =
  {
    overscrollBehavior: true,
  };

export const transformStyleProps: Record<keyof TransformStyleProps, boolean> = {
  transform: true,
  transformBox: true,
  transformOrigin: true,
  transformStyle: true,
};

/**
 * This object is a collection of all style prop keys that can be passed to
 * components. It is used to efficiently check if a prop passed to a
 * component is a style prop allowed by the design system.
 *
 * A Set data structure is not used because TypeScript doesn't directly
 * enforce the completeness of a Set based on an interface's keys.
 * In practice, this means that if a property is modified in any of the
 * interfaces that make up the `StyleProps` union, TypeScript would not
 * show an error in the Set definition. For that reason, we use an object
 * instead, where the value of each key doesn't matter, only the presence
 * of the key itself.
 */
export const stylePropKeys: Record<StylePropKeys, boolean> = {
  ...borderStyleProps,
  ...layoutStyleProps,
  ...positionStyleProps,
  ...scrollStyleProps,
  ...sizeStyleProps,
  ...spaceStyleProps,
  ...tableStyleProps,
  ...textStyleProps,
  ...backgroundStyleProps,
  ...boxShadowStyleProps,
  ...listStyleProps,
  ...mediaStyleProps,
  ...cursorStyleProps,
  ...gridContainerLayoutStyleProps,
  ...gridItemLayoutStyleProps,
  ...pointerEventsStyleProps,
  ...transitionStyleProps,
  ...opacityStyleProps,
  ...transformStyleProps,
  ...overscrollStyleProps,
};
